import { REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE } from '../../gpuLimits';
import {
  CONTACT_RECORD_ARM_A_OFFSET,
  CONTACT_RECORD_ARM_B_OFFSET,
  CONTACT_RECORD_CACHE_OFFSET,
  CONTACT_RECORD_CONSTRAINT_C0_OFFSET,
  CONTACT_RECORD_DUAL_OFFSET,
  CONTACT_RECORD_FLOATS,
  CONTACT_RECORD_META_OFFSET,
  CONTACT_RECORD_NORMAL_PEN_OFFSET,
  CONTACT_RECORD_PENALTY_OFFSET,
  CONTACT_RECORD_SHADOW_OFFSET,
  CONTACT_RECORD_VEC4S,
} from './contactRecord';

const U32_MAX = 0xffff_ffff;

export const EXACT_CONTACT_WORD_BITS = 32;
export const EXACT_CONTACT_LANES_PER_PLANE = 4;
export const EXACT_CONTACT_WORD_BYTES = EXACT_CONTACT_WORD_BITS / 8;
export const EXACT_CONTACT_PLANE_ROW_BYTES =
  EXACT_CONTACT_LANES_PER_PLANE * EXACT_CONTACT_WORD_BYTES;
export const LEGACY_CONTACT_AOS_ROW_BYTES = CONTACT_RECORD_FLOATS * EXACT_CONTACT_WORD_BYTES;

export const COLISEUM_510K_BODY_CAPACITY = 510_000;
export const COLISEUM_510K_CONTACT_ROW_CAPACITY = 7_340_032;
export const COLISEUM_510K_CONTACT_BINDING_LIMIT_BYTES = 128 * 1024 * 1024;

export type CanonicalContactPlaneKey =
  | 'meta'
  | 'normalPen'
  | 'armA'
  | 'armB'
  | 'constraintC0'
  | 'shadow'
  | 'dual'
  | 'penalty'
  | 'cache';

/**
 * `f32-bitcast-u32` means that the raw 32-bit payload is stored in a u32 lane
 * and recovered with bitcast<f32>. It is not a numerical conversion.
 */
export type CanonicalContactLaneEncoding = 'f32' | 'u32' | 'f32-bitcast-u32';
export type CanonicalContactPlaneStorageView = 'vec4f' | 'vec4u';

export interface CanonicalContactLaneDefinition {
  name: string;
  encoding: CanonicalContactLaneEncoding;
  bits: typeof EXACT_CONTACT_WORD_BITS;
}

export interface CanonicalContactPlaneDefinition {
  key: CanonicalContactPlaneKey;
  legacyVec4Offset: number;
  storageView: CanonicalContactPlaneStorageView;
  lanes: readonly CanonicalContactLaneDefinition[];
  /** Binding used when all nine canonical planes are exposed together. */
  canonicalBinding: number;
  /** Binding in the strict AVBD solver; shadow/cache are intentionally absent. */
  strictSolverBinding?: number;
}

const f32Lane = (name: string): CanonicalContactLaneDefinition => Object.freeze({
  name,
  encoding: 'f32',
  bits: EXACT_CONTACT_WORD_BITS,
});

const u32Lane = (name: string): CanonicalContactLaneDefinition => Object.freeze({
  name,
  encoding: 'u32',
  bits: EXACT_CONTACT_WORD_BITS,
});

const bitcastF32Lane = (name: string): CanonicalContactLaneDefinition => Object.freeze({
  name,
  encoding: 'f32-bitcast-u32',
  bits: EXACT_CONTACT_WORD_BITS,
});

/**
 * Canonical nine-plane contact ABI for the exact wide-body path.
 *
 * Every lane remains one complete 32-bit word. In particular, body endpoints
 * and encoded metadata are u32 rather than a packed 16+16 pair key, while the
 * cached tangent retains all f32 payload bits through a bitcast view.
 */
export const CANONICAL_CONTACT_PLANES: readonly CanonicalContactPlaneDefinition[] = Object.freeze([
  {
    key: 'meta',
    legacyVec4Offset: CONTACT_RECORD_META_OFFSET,
    storageView: 'vec4u',
    lanes: Object.freeze([
      u32Lane('bodyA'),
      u32Lane('bodyB'),
      u32Lane('manifoldSequencePlusOne'),
      u32Lane('encodedInfo'),
    ]),
    canonicalBinding: 0,
    strictSolverBinding: 0,
  },
  {
    key: 'normalPen',
    legacyVec4Offset: CONTACT_RECORD_NORMAL_PEN_OFFSET,
    storageView: 'vec4f',
    lanes: Object.freeze([
      f32Lane('normalX'),
      f32Lane('normalY'),
      f32Lane('normalZ'),
      f32Lane('penetration'),
    ]),
    canonicalBinding: 1,
    strictSolverBinding: 1,
  },
  {
    key: 'armA',
    legacyVec4Offset: CONTACT_RECORD_ARM_A_OFFSET,
    storageView: 'vec4f',
    lanes: Object.freeze([
      f32Lane('armAX'),
      f32Lane('armAY'),
      f32Lane('armAZ'),
      f32Lane('tangentAngle'),
    ]),
    canonicalBinding: 2,
    strictSolverBinding: 2,
  },
  {
    key: 'armB',
    legacyVec4Offset: CONTACT_RECORD_ARM_B_OFFSET,
    storageView: 'vec4f',
    lanes: Object.freeze([
      f32Lane('armBX'),
      f32Lane('armBY'),
      f32Lane('armBZ'),
      f32Lane('sphereArmFlags'),
    ]),
    canonicalBinding: 3,
    strictSolverBinding: 3,
  },
  {
    key: 'constraintC0',
    legacyVec4Offset: CONTACT_RECORD_CONSTRAINT_C0_OFFSET,
    storageView: 'vec4f',
    lanes: Object.freeze([
      f32Lane('normalC0'),
      f32Lane('tangentC0X'),
      f32Lane('tangentC0Y'),
      f32Lane('scratch'),
    ]),
    canonicalBinding: 4,
    strictSolverBinding: 4,
  },
  {
    key: 'shadow',
    legacyVec4Offset: CONTACT_RECORD_SHADOW_OFFSET,
    storageView: 'vec4f',
    lanes: Object.freeze([
      f32Lane('lambdaN'),
      f32Lane('lambdaT1'),
      f32Lane('lambdaT2'),
      f32Lane('frictionScale'),
    ]),
    canonicalBinding: 5,
  },
  {
    key: 'dual',
    legacyVec4Offset: CONTACT_RECORD_DUAL_OFFSET,
    storageView: 'vec4f',
    lanes: Object.freeze([
      f32Lane('dualN'),
      f32Lane('dualT1'),
      f32Lane('dualT2'),
      f32Lane('solverState'),
    ]),
    canonicalBinding: 6,
    strictSolverBinding: 5,
  },
  {
    key: 'penalty',
    legacyVec4Offset: CONTACT_RECORD_PENALTY_OFFSET,
    storageView: 'vec4f',
    lanes: Object.freeze([
      f32Lane('penaltyN'),
      f32Lane('penaltyT1'),
      f32Lane('penaltyT2'),
      f32Lane('frictionScale'),
    ]),
    canonicalBinding: 7,
    strictSolverBinding: 6,
  },
  {
    key: 'cache',
    legacyVec4Offset: CONTACT_RECORD_CACHE_OFFSET,
    storageView: 'vec4u',
    lanes: Object.freeze([
      u32Lane('cacheWord'),
      bitcastF32Lane('tangentT1X'),
      bitcastF32Lane('tangentT1Y'),
      bitcastF32Lane('tangentT1Z'),
    ]),
    canonicalBinding: 8,
  },
]);

export type StrictAvbdSolverResource =
  | CanonicalContactPlaneKey
  | 'bodyState'
  | 'graphMetadata'
  | 'contactCsr';

export interface StrictAvbdSolverBindingDefinition {
  binding: number;
  resource: StrictAvbdSolverResource;
  kind: 'contact-plane' | 'external';
}

/** Seven contact planes plus three merged external resources: exactly ten. */
export const STRICT_AVBD_SOLVER_BINDINGS: readonly StrictAvbdSolverBindingDefinition[] = Object.freeze([
  { binding: 0, resource: 'meta', kind: 'contact-plane' },
  { binding: 1, resource: 'normalPen', kind: 'contact-plane' },
  { binding: 2, resource: 'armA', kind: 'contact-plane' },
  { binding: 3, resource: 'armB', kind: 'contact-plane' },
  { binding: 4, resource: 'constraintC0', kind: 'contact-plane' },
  { binding: 5, resource: 'dual', kind: 'contact-plane' },
  { binding: 6, resource: 'penalty', kind: 'contact-plane' },
  { binding: 7, resource: 'bodyState', kind: 'external' },
  { binding: 8, resource: 'graphMetadata', kind: 'external' },
  { binding: 9, resource: 'contactCsr', kind: 'external' },
]);

export interface ContactStorageLimitStatus {
  checked: boolean;
  ok: boolean;
  maxStorageBufferBindingSize?: number;
  maxBufferSize?: number;
  exceedsMaxStorageBufferBindingSize: boolean;
  exceedsMaxBufferSize: boolean;
}

export interface LegacyContactAosFieldLayout {
  key: CanonicalContactPlaneKey;
  vec4Offset: number;
  byteOffsetInRow: number;
  bytes: number;
}

export interface ExactContactSoaPlaneLayout {
  key: CanonicalContactPlaneKey;
  binding: number;
  strictSolverBinding?: number;
  storageView: CanonicalContactPlaneStorageView;
  rowStrideBytes: number;
  elementCount: number;
  bytes: number;
  /** Offset in a conceptual contiguous dump; the actual plane binding starts at byte zero. */
  logicalByteOffset: number;
  bindingByteOffset: 0;
  limitStatus: ContactStorageLimitStatus;
}

export interface CanonicalContactStorageLayout {
  rowCapacity: number;
  wordBits: typeof EXACT_CONTACT_WORD_BITS;
  lanesPerPlane: typeof EXACT_CONTACT_LANES_PER_PLANE;
  legacyAoS: {
    bindingCount: 1;
    rowStrideBytes: number;
    totalBytes: number;
    fields: readonly LegacyContactAosFieldLayout[];
    limitStatus: ContactStorageLimitStatus;
  };
  exactSoA: {
    bindingCount: number;
    planeRowStrideBytes: number;
    planeBytes: number;
    totalBytes: number;
    maxRowsPerBinding: number;
    planes: readonly ExactContactSoaPlaneLayout[];
    limitStatus: ContactStorageLimitStatus;
  };
  strictSolver: {
    bindings: typeof STRICT_AVBD_SOLVER_BINDINGS;
    bindingCount: number;
    requestedBindingBudget: number;
    withinRequestedBindingBudget: boolean;
  };
}

export interface CanonicalContactWordAddress {
  binding: number;
  byteOffset: number;
}

export interface CanonicalContactStorageSelfTestResult {
  ok: boolean;
  checks: number;
  failures: string[];
  coliseum510k: {
    bodies: number;
    contactRows: number;
    legacyAosBytes: number;
    planeBytes: number;
    exactSoaBytes: number;
    planeBindingCount: number;
    strictSolverBindingCount: number;
    maxRowsPer128MiBPlane: number;
  };
}

function positiveSafeInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new Error(`${label} must be a positive safe integer (received ${String(value)}).`);
  }
  return value;
}

function checkedProduct(label: string, ...factors: number[]): number {
  let result = 1;
  for (const factor of factors) {
    result *= factor;
    if (!Number.isSafeInteger(result)) throw new Error(`${label} exceeds exact integer range.`);
  }
  return result;
}

function checkedSum(label: string, ...terms: number[]): number {
  const result = terms.reduce((sum, term) => sum + term, 0);
  if (!Number.isSafeInteger(result)) throw new Error(`${label} exceeds exact integer range.`);
  return result;
}

function limitStatus(
  bytes: number,
  limits?: { maxStorageBufferBindingSize: number; maxBufferSize: number },
): ContactStorageLimitStatus {
  if (!limits) {
    return Object.freeze({
      checked: false,
      ok: true,
      exceedsMaxStorageBufferBindingSize: false,
      exceedsMaxBufferSize: false,
    });
  }
  const maxStorageBufferBindingSize = positiveSafeInteger(
    limits.maxStorageBufferBindingSize,
    'maxStorageBufferBindingSize',
  );
  const maxBufferSize = positiveSafeInteger(limits.maxBufferSize, 'maxBufferSize');
  const exceedsMaxStorageBufferBindingSize = bytes > maxStorageBufferBindingSize;
  const exceedsMaxBufferSize = bytes > maxBufferSize;
  return Object.freeze({
    checked: true,
    ok: !exceedsMaxStorageBufferBindingSize && !exceedsMaxBufferSize,
    maxStorageBufferBindingSize,
    maxBufferSize,
    exceedsMaxStorageBufferBindingSize,
    exceedsMaxBufferSize,
  });
}

/**
 * GPU-free capacity/address plan. No TypedArray or GPUBuffer proportional to
 * rowCapacity is allocated here.
 */
export function planCanonicalContactStorageLayout(
  requestedRowCapacity: number,
  limits?: { maxStorageBufferBindingSize: number; maxBufferSize: number },
): CanonicalContactStorageLayout {
  const rowCapacity = positiveSafeInteger(requestedRowCapacity, 'contact row capacity');
  if (rowCapacity > U32_MAX) {
    throw new Error(`contact row capacity exceeds the exact u32 row ABI (${rowCapacity}).`);
  }
  const planeBytes = checkedProduct(
    'canonical contact plane byte length',
    rowCapacity,
    EXACT_CONTACT_PLANE_ROW_BYTES,
  );
  const totalBytes = checkedProduct(
    'canonical contact storage byte length',
    planeBytes,
    CANONICAL_CONTACT_PLANES.length,
  );
  const legacyTotalBytes = checkedProduct(
    'legacy contact AoS byte length',
    rowCapacity,
    LEGACY_CONTACT_AOS_ROW_BYTES,
  );
  if (legacyTotalBytes !== totalBytes) {
    throw new Error('Canonical contact SoA lost or duplicated legacy 32-bit fields.');
  }

  const fields = CANONICAL_CONTACT_PLANES.map((plane): LegacyContactAosFieldLayout =>
    Object.freeze({
      key: plane.key,
      vec4Offset: plane.legacyVec4Offset,
      byteOffsetInRow: plane.legacyVec4Offset * EXACT_CONTACT_PLANE_ROW_BYTES,
      bytes: EXACT_CONTACT_PLANE_ROW_BYTES,
    }));
  const planes = CANONICAL_CONTACT_PLANES.map((plane): ExactContactSoaPlaneLayout =>
    Object.freeze({
      key: plane.key,
      binding: plane.canonicalBinding,
      strictSolverBinding: plane.strictSolverBinding,
      storageView: plane.storageView,
      rowStrideBytes: EXACT_CONTACT_PLANE_ROW_BYTES,
      elementCount: rowCapacity,
      bytes: planeBytes,
      logicalByteOffset: checkedProduct(
        `${plane.key} logical plane byte offset`,
        plane.canonicalBinding,
        planeBytes,
      ),
      bindingByteOffset: 0,
      limitStatus: limitStatus(planeBytes, limits),
    }));
  const exactLimitStatus = Object.freeze({
    ...(planes[0]?.limitStatus ?? limitStatus(planeBytes, limits)),
    ok: planes.every((plane) => plane.limitStatus.ok),
  });
  const effectiveBindingLimit = limits
    ? Math.min(limits.maxStorageBufferBindingSize, limits.maxBufferSize)
    : Number.MAX_SAFE_INTEGER;

  return Object.freeze({
    rowCapacity,
    wordBits: EXACT_CONTACT_WORD_BITS,
    lanesPerPlane: EXACT_CONTACT_LANES_PER_PLANE,
    legacyAoS: Object.freeze({
      bindingCount: 1,
      rowStrideBytes: LEGACY_CONTACT_AOS_ROW_BYTES,
      totalBytes: legacyTotalBytes,
      fields: Object.freeze(fields),
      limitStatus: limitStatus(legacyTotalBytes, limits),
    }),
    exactSoA: Object.freeze({
      bindingCount: planes.length,
      planeRowStrideBytes: EXACT_CONTACT_PLANE_ROW_BYTES,
      planeBytes,
      totalBytes,
      maxRowsPerBinding: Math.floor(effectiveBindingLimit / EXACT_CONTACT_PLANE_ROW_BYTES),
      planes: Object.freeze(planes),
      limitStatus: exactLimitStatus,
    }),
    strictSolver: Object.freeze({
      bindings: STRICT_AVBD_SOLVER_BINDINGS,
      bindingCount: STRICT_AVBD_SOLVER_BINDINGS.length,
      requestedBindingBudget: REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE,
      withinRequestedBindingBudget:
        STRICT_AVBD_SOLVER_BINDINGS.length <= REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE,
    }),
  });
}

function checkedRowAndLane(
  layout: CanonicalContactStorageLayout,
  row: number,
  lane: number,
): void {
  if (!Number.isSafeInteger(row) || row < 0 || row >= layout.rowCapacity) {
    throw new Error(`contact row ${String(row)} is outside [0, ${layout.rowCapacity}).`);
  }
  if (!Number.isSafeInteger(lane) || lane < 0 || lane >= EXACT_CONTACT_LANES_PER_PLANE) {
    throw new Error(`contact lane ${String(lane)} is outside [0, 4).`);
  }
}

function planeDefinition(key: CanonicalContactPlaneKey): CanonicalContactPlaneDefinition {
  const plane = CANONICAL_CONTACT_PLANES.find((candidate) => candidate.key === key);
  if (!plane) throw new Error(`Unknown canonical contact plane: ${String(key)}.`);
  return plane;
}

/** Byte address inside the legacy single AoS binding. */
export function legacyContactWordByteOffset(
  layout: CanonicalContactStorageLayout,
  row: number,
  planeKey: CanonicalContactPlaneKey,
  lane: number,
): number {
  checkedRowAndLane(layout, row, lane);
  const plane = planeDefinition(planeKey);
  return checkedSum(
    'legacy contact word byte offset',
    checkedProduct('legacy contact row byte offset', row, layout.legacyAoS.rowStrideBytes),
    checkedProduct('legacy contact plane byte offset', plane.legacyVec4Offset, EXACT_CONTACT_PLANE_ROW_BYTES),
    lane * EXACT_CONTACT_WORD_BYTES,
  );
}

/** Binding plus byte address inside one exact SoA plane. */
export function exactContactSoaWordAddress(
  layout: CanonicalContactStorageLayout,
  row: number,
  planeKey: CanonicalContactPlaneKey,
  lane: number,
): CanonicalContactWordAddress {
  checkedRowAndLane(layout, row, lane);
  const plane = planeDefinition(planeKey);
  return Object.freeze({
    binding: plane.canonicalBinding,
    byteOffset: checkedSum(
      'exact contact SoA word byte offset',
      checkedProduct('exact contact SoA row byte offset', row, EXACT_CONTACT_PLANE_ROW_BYTES),
      lane * EXACT_CONTACT_WORD_BYTES,
    ),
  });
}

/** Allocation-light proof of the 9xvec4x32 ABI and the 510K binding boundary. */
export function runCanonicalContactStorageLayoutSelfTests(): CanonicalContactStorageSelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };
  const limits = {
    maxStorageBufferBindingSize: COLISEUM_510K_CONTACT_BINDING_LIMIT_BYTES,
    maxBufferSize: 256 * 1024 * 1024,
  };
  const target = planCanonicalContactStorageLayout(
    COLISEUM_510K_CONTACT_ROW_CAPACITY,
    limits,
  );

  check(
    'legacy contact ABI constants changed',
    CONTACT_RECORD_VEC4S === 9
      && CONTACT_RECORD_FLOATS === 36
      && LEGACY_CONTACT_AOS_ROW_BYTES === 144,
  );
  check(
    'canonical plane count or bindings changed',
    CANONICAL_CONTACT_PLANES.length === 9
      && target.exactSoA.bindingCount === 9
      && CANONICAL_CONTACT_PLANES.every((plane, index) => plane.canonicalBinding === index),
  );
  check(
    'legacy vec4 offsets diverged from canonical order',
    CANONICAL_CONTACT_PLANES.every((plane, index) => plane.legacyVec4Offset === index),
  );
  check(
    'canonical contact lane was narrowed below 32 bits',
    CANONICAL_CONTACT_PLANES.every(
      (plane) => plane.lanes.length === 4 && plane.lanes.every((lane) => lane.bits === 32),
    ),
  );
  check(
    'wide body endpoints are not complete u32 fields',
    CANONICAL_CONTACT_PLANES[0].lanes[0].encoding === 'u32'
      && CANONICAL_CONTACT_PLANES[0].lanes[1].encoding === 'u32'
      && COLISEUM_510K_BODY_CAPACITY <= U32_MAX,
  );
  check('510K exact plane byte count changed', target.exactSoA.planeBytes === 117_440_512);
  check('510K legacy AoS byte count changed', target.legacyAoS.totalBytes === 1_056_964_608);
  check(
    'SoA changed canonical payload size',
    target.exactSoA.totalBytes === 1_056_964_608
      && target.exactSoA.totalBytes === target.legacyAoS.totalBytes,
  );
  check(
    '510K plane no longer fits one 128 MiB binding',
    target.exactSoA.limitStatus.ok
      && target.exactSoA.planes.every((plane) => plane.limitStatus.ok),
  );
  check(
    'legacy 510K AoS unexpectedly fits a single binding',
    !target.legacyAoS.limitStatus.ok
      && target.legacyAoS.limitStatus.exceedsMaxStorageBufferBindingSize
      && target.legacyAoS.limitStatus.exceedsMaxBufferSize,
  );
  check(
    '128 MiB plane row boundary changed',
    target.exactSoA.maxRowsPerBinding === 8_388_608
      && target.exactSoA.maxRowsPerBinding - target.rowCapacity === 1_048_576,
  );
  check(
    'plane logical offsets overlap or leave gaps',
    target.exactSoA.planes.every(
      (plane, index) => plane.logicalByteOffset === index * target.exactSoA.planeBytes,
    )
      && (target.exactSoA.planes.at(-1)?.logicalByteOffset ?? -1)
        + target.exactSoA.planeBytes === target.exactSoA.totalBytes,
  );
  check(
    'strict AVBD solver binding contract changed',
    target.strictSolver.bindingCount === 10
      && target.strictSolver.withinRequestedBindingBudget
      && target.strictSolver.bindingCount === target.strictSolver.requestedBindingBudget
      && STRICT_AVBD_SOLVER_BINDINGS.every((binding, index) => binding.binding === index),
  );
  check(
    'strict solver must exclude shadow/cache and include dual/penalty',
    !STRICT_AVBD_SOLVER_BINDINGS.some((binding) => binding.resource === 'shadow')
      && !STRICT_AVBD_SOLVER_BINDINGS.some((binding) => binding.resource === 'cache')
      && STRICT_AVBD_SOLVER_BINDINGS[5]?.resource === 'dual'
      && STRICT_AVBD_SOLVER_BINDINGS[6]?.resource === 'penalty',
  );

  const addressProbe = planCanonicalContactStorageLayout(8);
  const legacyAddress = legacyContactWordByteOffset(addressProbe, 3, 'penalty', 2);
  const soaAddress = exactContactSoaWordAddress(addressProbe, 3, 'penalty', 2);
  check(
    'legacy AoS address mapping changed',
    legacyAddress === 3 * 144 + CONTACT_RECORD_PENALTY_OFFSET * 16 + 2 * 4,
  );
  check(
    'exact SoA address mapping changed',
    soaAddress.binding === 7 && soaAddress.byteOffset === 3 * 16 + 2 * 4,
  );

  const exactBoundary = planCanonicalContactStorageLayout(8_388_608, {
    maxStorageBufferBindingSize: COLISEUM_510K_CONTACT_BINDING_LIMIT_BYTES,
    maxBufferSize: COLISEUM_510K_CONTACT_BINDING_LIMIT_BYTES,
  });
  const oneRowOver = planCanonicalContactStorageLayout(8_388_609, {
    maxStorageBufferBindingSize: COLISEUM_510K_CONTACT_BINDING_LIMIT_BYTES,
    maxBufferSize: COLISEUM_510K_CONTACT_BINDING_LIMIT_BYTES + EXACT_CONTACT_PLANE_ROW_BYTES,
  });
  check('exact 128 MiB plane boundary must pass', exactBoundary.exactSoA.limitStatus.ok);
  check(
    'one-row plane binding overflow classification changed',
    !oneRowOver.exactSoA.limitStatus.ok
      && oneRowOver.exactSoA.planes.every(
        (plane) => plane.limitStatus.exceedsMaxStorageBufferBindingSize
          && !plane.limitStatus.exceedsMaxBufferSize,
      ),
  );

  let invalidCapacityRejected = false;
  try {
    planCanonicalContactStorageLayout(0);
  } catch {
    invalidCapacityRejected = true;
  }
  check('zero contact row capacity was accepted', invalidCapacityRejected);

  return {
    ok: failures.length === 0,
    checks,
    failures,
    coliseum510k: {
      bodies: COLISEUM_510K_BODY_CAPACITY,
      contactRows: target.rowCapacity,
      legacyAosBytes: target.legacyAoS.totalBytes,
      planeBytes: target.exactSoA.planeBytes,
      exactSoaBytes: target.exactSoA.totalBytes,
      planeBindingCount: target.exactSoA.bindingCount,
      strictSolverBindingCount: target.strictSolver.bindingCount,
      maxRowsPer128MiBPlane: target.exactSoA.maxRowsPerBinding,
    },
  };
}
